// #130/#152 Kriter çubuğu — 3 tam-genişlik segment:
//   1) Distribütör Bilgileri: Distribütör (Vendor, ZORUNLU — #214) + Depo + Planlanacak Araçlar
//   2) Sipariş Bilgileri: sipariş/sevk tarih aralıkları
//   3) Müşteri Bilgileri: temsilci / grup / ek grup / özel grup / özel alan / Haritadan Alan Seç
// Distribütör seçilmeden Listele yapılamaz (#152 madde 11). Listele kriterleri
// POST /api/shipmentplans/pending-orders'a gönderir.
import { useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders } from '../listPageStyles';
import { SearchableSelect } from '../ui';
import { MultiChipSelect } from '../campaign/MultiChipSelect';
import { RELATION_CONFIGS } from '../relationConfigs';
import { resolveLabels } from '../campaign/campaignUtils';
import { unwrapLookupItems } from '../c360';
import ShipmentAreaMap from '../shipmentVehicle/ShipmentAreaMap';
import { makeLookupFetch } from './shipmentPlanningUtils';
import { s } from '../listPageStyles';

const unwrap = (p) => (p && (p.data ?? p.Data)) ?? p;

// Id dizisini chip {value,label}'a çözer (ShipmentCriteriaTab kalıbı).
function useChipItems(ids, endpoint, setIds) {
  const [items, setItems] = useState([]);
  const loadedRef = useRef(null);
  useEffect(() => {
    const sig = (ids || []).join(',');
    if (sig === loadedRef.current) return;
    loadedRef.current = sig;
    if (!ids || !ids.length) { setItems([]); return undefined; }
    let alive = true;
    resolveLabels(endpoint, ids)
      .then((map) => { if (alive) setItems(ids.map((id) => ({ value: id, label: map[id] || id }))); })
      .catch(() => { if (alive) setItems(ids.map((id) => ({ value: id, label: id }))); });
    return () => { alive = false; };
  }, [ids, endpoint]);
  const onChange = (next) => {
    loadedRef.current = next.map((x) => x.value).join(',');
    setItems(next);
    setIds(next.map((x) => x.value));
  };
  return [items, onChange];
}

const fieldLabel = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary,#475569)', marginBottom: 5, display: 'block' };
const dateInput = {
  padding: '8px 8px', borderRadius: 10, border: '1px solid var(--border-color,#cbd5e1)',
  background: 'var(--input-bg,#fff)', color: 'var(--text-primary,#1f2937)', fontSize: 12.5,
  flex: 1, minWidth: 0, boxSizing: 'border-box',
};

// Görsel segment (fieldset) sarmalayıcısı: tam genişlik, sola hizalı küçük başlık + ince ayraç.
const segmentBox = {
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12,
  padding: '12px 14px 14px', background: 'var(--bg-subtle,#f8fafc)', minWidth: 0, margin: 0,
};
const segmentTitle = {
  fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--text-secondary,#64748b)', marginBottom: 10, paddingBottom: 8,
  borderBottom: '1px solid var(--border-color,#e5e7eb)', display: 'block', width: '100%',
};
// Segment içi alan ızgarası — dar ekranda otomatik alt alta, geniş ekranda yatay dolu.
const segmentGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12, alignItems: 'start',
};

export default function CriteriaBar({ criteria, setCriteria, onList, listing, vendorLocked, readOnly, onReset }) {
  const c = criteria;
  const patch = (k, v) => setCriteria((prev) => ({ ...prev, [k]: v }));
  const [collapsed, setCollapsed] = useState(false);

  const [grpItems, setGrpItems] = useChipItems(c.customerGroupIds, '/api/customergroups', (ids) => patch('customerGroupIds', ids));
  const [subItems, setSubItems] = useChipItems(c.subGroupIds, '/api/customersubgroups', (ids) => patch('subGroupIds', ids));
  const [dynItems, setDynItems] = useChipItems(c.dynamicGroupIds, '/api/customerdynamicgroups', (ids) => patch('dynamicGroupIds', ids));
  const [repItems, setRepItems] = useChipItems(c.salesRepIds, '/api/representatives', (ids) => patch('salesRepIds', ids));

  // #214 Distribütör alanı gerçek Distribütör (Vendor) tanımlarını listeler — Dağıtıcı DEĞİL.
  const vendorFetch = useMemo(() => makeLookupFetch('/api/vendors', { authHeaders }), []);
  // Depo, seçili distribütörna bağlıdır: yalnız o kanalın depoları listelenir (Warehouse.VendorId).
  // Kanal seçilmeden alan zaten kapalıdır, dolayısıyla filtresiz istek atılmaz.
  const whFetch = useMemo(
    () => makeLookupFetch('/api/warehouses', { authHeaders, baseFilter: c.vendorId ? `VendorId eq ${c.vendorId}` : null }),
    [c.vendorId],
  );

  const hasVendor = !!c.vendorId;

  // Distribütör (Vendor) değişince bağımlı seçimleri (depo + araçlar) sıfırla.
  const onVendorChange = (v, item) => {
    setCriteria((prev) => ({
      ...prev,
      vendorId: v || null,
      vendorName: item?.label || '',
      warehouseId: null,
      warehouseName: '',
      vehicleIds: [],
    }));
  };

  // ── Planlanacak Araçlar (#152 madde 5) — seçili distribütörün araçları ──────
  const [vehicleOptions, setVehicleOptions] = useState([]);
  useEffect(() => {
    if (!c.vendorId) { setVehicleOptions([]); return undefined; }
    let alive = true;
    fetch(`/api/shipmentplans/vehicles?vendorId=${encodeURIComponent(c.vendorId)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (alive) setVehicleOptions((unwrap(d)?.vehicles) || []); })
      .catch(() => { if (alive) setVehicleOptions([]); });
    return () => { alive = false; };
  }, [c.vendorId]);
  const vehicleIds = useMemo(() => c.vehicleIds || [], [c.vehicleIds]);
  const toggleVehicle = (id) => patch('vehicleIds', vehicleIds.includes(id) ? vehicleIds.filter((x) => x !== id) : [...vehicleIds, id]);
  const allVehiclesSelected = vehicleOptions.length > 0 && vehicleIds.length === vehicleOptions.length;
  const toggleAllVehicles = () => patch('vehicleIds', allVehiclesSelected ? [] : vehicleOptions.map((v) => v.id));

  // Özel alanlar
  const [fieldDefs, setFieldDefs] = useState([]);
  const [draftFieldId, setDraftFieldId] = useState('');
  const [draftValue, setDraftValue] = useState('');
  useEffect(() => {
    fetch('/api/customeradditionalfields/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setFieldDefs(unwrapLookupItems(d)))
      .catch(() => setFieldDefs([]));
  }, []);
  const addlFields = c.additionalFields || [];
  const addAdditional = () => {
    if (!draftFieldId || !draftValue.trim()) return;
    const def = fieldDefs.find((f) => f.id === draftFieldId);
    patch('additionalFields', [...addlFields, { fieldId: draftFieldId, fieldName: def?.name || def?.textId || draftFieldId, value: draftValue.trim() }]);
    setDraftFieldId(''); setDraftValue('');
  };
  const removeAdditional = (idx) => patch('additionalFields', addlFields.filter((_, i) => i !== idx));

  // Harita alanı modalı — taban müşteriler: seçili distribütörün BEKLEYEN siparişi olanlar (#152 madde 9).
  const [areaOpen, setAreaOpen] = useState(false);
  const [baseCustomers, setBaseCustomers] = useState([]);
  const [baseLoading, setBaseLoading] = useState(false);
  const polygons = useMemo(() => c.shipmentArea?.polygons || [], [c.shipmentArea]);
  const setPolygons = (next) => patch('shipmentArea', { ...(c.shipmentArea || {}), polygons: next });
  // Dağıtım kanalı/depo değişince taban müşterileri yeniden yükle.
  const baseSig = `${c.vendorId || ''}|${c.warehouseId || ''}`;
  const loadedSigRef = useRef(null);
  useEffect(() => {
    if (!areaOpen) return undefined;
    if (loadedSigRef.current === baseSig && baseCustomers.length) return undefined;
    let alive = true;
    setBaseLoading(true);
    const params = [];
    if (c.vendorId) params.push(`vendorId=${encodeURIComponent(c.vendorId)}`);
    if (c.warehouseId) params.push(`warehouseId=${encodeURIComponent(c.warehouseId)}`);
    fetch(`/api/shipmentplans/pending-customers${params.length ? `?${params.join('&')}` : ''}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (!alive) return;
        const rows = (unwrap(d) || []).map((x) => ({
          id: x.id, code: x.textId, label: x.title,
          lat: Number(x.latitude), lng: Number(x.longitude),
          neighborhood: x.neighborhood, street: x.street,
        }));
        loadedSigRef.current = baseSig;
        setBaseCustomers(rows);
      })
      .catch(() => { if (alive) setBaseCustomers([]); })
      .finally(() => { if (alive) setBaseLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaOpen, baseSig]);

  const areaCount = polygons.length;

  // Daraltılmış görünüm için aktif filtre özeti.
  const grpCount = (c.customerGroupIds?.length || 0) + (c.subGroupIds?.length || 0) + (c.dynamicGroupIds?.length || 0);
  const summaryBits = [];
  if (c.vendorName) summaryBits.push(c.vendorName);
  if (c.warehouseName) summaryBits.push(c.warehouseName);
  if (vehicleIds.length) summaryBits.push(`${vehicleIds.length} araç`);
  if (grpCount) summaryBits.push(`${grpCount} grup`);
  if (c.salesRepIds?.length) summaryBits.push(`${c.salesRepIds.length} temsilci`);
  if (addlFields.length) summaryBits.push(`${addlFields.length} özel alan`);
  if (areaCount) summaryBits.push(`${areaCount} alan`);
  const summaryText = summaryBits.length ? summaryBits.join(' · ') : 'Filtre seçilmedi';

  return (
    <div style={{
      background: 'var(--bg-card,#fff)', border: '1px solid var(--border-color,#e5e7eb)',
      borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-card, 0 6px 18px rgba(15,23,42,0.06))',
    }}>
      {/* Başlık: daralt/genişlet + özet + sıfırla */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: collapsed ? 0 : 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setCollapsed((v) => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 800, fontSize: 13.5, color: 'var(--text-primary,#0f172a)', padding: 0 }}>
          <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--text-secondary,#64748b)' }}>{collapsed ? '▸' : '▾'}</span>
          Filtreler
        </button>
        {collapsed && (
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>
            {summaryText}
          </span>
        )}
        <button type="button" onClick={onReset} disabled={readOnly}
          style={{ ...s.btnSecondary, marginLeft: 'auto', padding: '6px 12px', fontSize: 12, opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}>
          Sıfırla
        </button>
      </div>

      {!collapsed && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Segment 1 — Distribütör Bilgileri (tam genişlik) */}
        <fieldset style={segmentBox}>
          <legend style={segmentTitle}>Distribütör Bilgileri</legend>
          <div style={segmentGrid}>
            {/* Distribütör — uzun kanal adları sığsın diye iki sütun genişliğinde. */}
            <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
              <label style={fieldLabel}>
                Distribütör <span style={{ color: 'var(--danger)' }}>*</span>
                <span style={{ fontWeight: 500, color: '#94a3b8' }}> (zorunlu)</span>
              </label>
              <div style={!hasVendor ? { borderRadius: 10, boxShadow: '0 0 0 2px rgba(226,68,92,0.25)' } : undefined}>
                <SearchableSelect
                  value={c.vendorId || ''}
                  selectedLabel={c.vendorName || ''}
                  onChange={onVendorChange}
                  fetchOptions={vendorFetch}
                  pageSize={20}
                  placeholder="Dağıtım kanalı seçin…"
                  emptyOption="(Seçiniz)"
                  disabled={vendorLocked || readOnly}
                />
              </div>
            </div>

            {/* Depo — seçili distribütörnın depoları; kanal seçilmeden kapalı. */}
            <div style={{ minWidth: 0 }}>
              <label style={fieldLabel}>
                Depo
                <span style={{ fontWeight: 500, color: '#94a3b8' }}>
                  {hasVendor ? ' (boş = Tümü)' : ' (önce distribütör seçin)'}
                </span>
              </label>
              <SearchableSelect
                key={c.vendorId || 'no-vendor'}
                value={c.warehouseId || ''}
                selectedLabel={c.warehouseName || ''}
                onChange={(v, item) => { patch('warehouseId', v || null); patch('warehouseName', item?.label || ''); }}
                fetchOptions={whFetch}
                pageSize={20}
                placeholder={hasVendor ? 'Depo ara…' : 'Önce distribütör seçin'}
                emptyOption="(Tümü)"
                disabled={readOnly || !hasVendor}
              />
            </div>

            {/* Planlanacak Araçlar — seçili distribütörün araçları (#152 madde 5) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={fieldLabel}>
                Planlanacak Araçlar
                <span style={{ fontWeight: 500, color: '#94a3b8' }}> {hasVendor ? `(${vehicleIds.length}/${vehicleOptions.length} seçili — boş = tümü)` : '(önce distribütör seçin)'}</span>
              </label>
              {!hasVendor ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary,#94a3b8)', padding: '8px 0' }}>Araç listesi için önce distribütör seçin.</div>
              ) : vehicleOptions.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary,#94a3b8)', padding: '8px 0' }}>Bu distribütörna tanımlı aktif araç bulunamadı.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
                  <button type="button" onClick={toggleAllVehicles} disabled={readOnly}
                    style={vehicleChip(allVehiclesSelected, readOnly, true)}>
                    {allVehiclesSelected ? '✓ Tümü' : 'Tümünü Seç'}
                  </button>
                  {vehicleOptions.map((v) => {
                    const sel = vehicleIds.includes(v.id);
                    return (
                      <button key={v.id} type="button" onClick={() => toggleVehicle(v.id)} disabled={readOnly}
                        title={[v.plaka, [v.brand, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                        style={vehicleChip(sel, readOnly, false)}>
                        <span aria-hidden="true">🚚</span> {v.plaka}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </fieldset>

        {/* Segment 2 — Sipariş Bilgileri (tam genişlik) */}
        <fieldset style={segmentBox}>
          <legend style={segmentTitle}>Sipariş Bilgileri</legend>
          <div style={segmentGrid}>
            <div>
              <label style={fieldLabel}>Sipariş Tarih Aralığı</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="date" style={dateInput} value={c.orderDateFrom || ''} onChange={(e) => patch('orderDateFrom', e.target.value)} />
                <span style={{ color: '#94a3b8' }}>–</span>
                <input type="date" style={dateInput} value={c.orderDateTo || ''} onChange={(e) => patch('orderDateTo', e.target.value)} />
              </div>
            </div>

            <div>
              <label style={fieldLabel}>Sevk Tarih Aralığı <span style={{ fontWeight: 500, color: '#94a3b8' }}>(boş = Tümü)</span></label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="date" style={dateInput} value={c.shipDateFrom || ''} onChange={(e) => patch('shipDateFrom', e.target.value)} />
                <span style={{ color: '#94a3b8' }}>–</span>
                <input type="date" style={dateInput} value={c.shipDateTo || ''} onChange={(e) => patch('shipDateTo', e.target.value)} />
              </div>
            </div>
          </div>
        </fieldset>

        {/* Segment 3 — Müşteri Bilgileri (tam genişlik) */}
        <fieldset style={segmentBox}>
          <legend style={segmentTitle}>Müşteri Bilgileri</legend>
          <div style={segmentGrid}>
            <div>
              <label style={fieldLabel}>Satış Temsilcisi</label>
              <MultiChipSelect items={repItems} onChange={setRepItems} config={RELATION_CONFIGS.representative} placeholder="Temsilci ekle…" />
            </div>

            <div>
              <label style={fieldLabel}>Müşteri Grubu</label>
              <MultiChipSelect items={grpItems} onChange={setGrpItems} config={RELATION_CONFIGS.customerGroup} placeholder="Grup ekle…" />
            </div>

            <div>
              <label style={fieldLabel}>Ek Grup</label>
              <MultiChipSelect items={subItems} onChange={setSubItems} config={RELATION_CONFIGS.customerSubGroup} placeholder="Ek grup ekle…" />
            </div>

            <div>
              <label style={fieldLabel}>Özel Grup</label>
              <MultiChipSelect items={dynItems} onChange={setDynItems} config={RELATION_CONFIGS.customerDynamicGroup} placeholder="Özel grup ekle…" />
            </div>

            {/* Özel Alan — "+" kaldırıldı (#152 madde 13); değer girip Enter ile eklenir. */}
            <div>
              <label style={fieldLabel}>Özel Alan</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={draftFieldId} onChange={(e) => setDraftFieldId(e.target.value)}
                  style={{ flex: 1, minWidth: 0, padding: '8px 8px', borderRadius: 10, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 12 }}>
                  <option value="">Alan…</option>
                  {fieldDefs.map((f) => <option key={f.id} value={f.id}>{f.name || f.textId}</option>)}
                </select>
                <input value={draftValue} onChange={(e) => setDraftValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdditional(); } }}
                  placeholder="Değer (Enter)" style={{ width: 110, padding: '8px 8px', borderRadius: 10, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 12 }} />
              </div>
              {addlFields.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {addlFields.map((f, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 999,
                      fontSize: 11, background: 'var(--bg-info,#e6f1fb)', color: 'var(--text-info,#185fa5)', border: '1px solid rgba(24,95,165,0.2)',
                    }}>
                      {f.fieldName} = {f.value}
                      <button type="button" onClick={() => removeAdditional(i)} aria-label="Kaldır"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Haritadan Alan Seç — yalnız distribütör seçiliyken (bekleyen sipariş tabanı). */}
            <div>
              <label style={fieldLabel}>Sevkiyat Alanı</label>
              <button type="button" onClick={() => setAreaOpen(true)} disabled={readOnly || !hasVendor}
                title={!hasVendor ? 'Önce distribütör seçin' : undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 12,
                  border: `1px solid ${areaCount ? '#2563eb' : 'var(--border-color,#cbd5e1)'}`,
                  background: areaCount ? 'rgba(37,99,235,0.08)' : 'var(--input-bg,#fff)',
                  color: areaCount ? '#2563eb' : 'var(--text-primary,#334155)', fontWeight: 700, fontSize: 13,
                  cursor: (readOnly || !hasVendor) ? 'not-allowed' : 'pointer', opacity: (readOnly || !hasVendor) ? 0.5 : 1, width: '100%', justifyContent: 'center',
                }}>
                <span aria-hidden="true">🗺</span> Haritadan Alan Seç{areaCount ? ` (${areaCount})` : ''}
              </button>
            </div>
          </div>
        </fieldset>
      </div>
      )}

      {/* Aksiyon satırı — zorunlu uyarısı + Listele */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
        {readOnly ? (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary,#64748b)' }}>
            Bu plan dönüştürüldü (salt-okunur) — yeniden listelenemez.
          </span>
        ) : !hasVendor && (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true">⚠️</span> Distribütör seçmeden listeleme ve planlama yapılamaz.
          </span>
        )}

        <button
          type="button"
          onClick={onList}
          disabled={listing || readOnly || !hasVendor}
          title={!hasVendor ? 'Önce Distribütör seçin' : readOnly ? 'Dönüştürülmüş plan yeniden listelenemez' : undefined}
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 30px', borderRadius: 14, border: 'none',
            background: (listing || readOnly || !hasVendor) ? '#93c5fd' : 'linear-gradient(135deg, var(--accent) 0%, #0ea5e9 100%)',
            color: '#fff', fontWeight: 900, fontSize: 15, letterSpacing: 0.5,
            cursor: (readOnly || !hasVendor) ? 'not-allowed' : listing ? 'wait' : 'pointer', opacity: (readOnly || !hasVendor) ? 0.6 : 1,
            boxShadow: '0 12px 26px rgba(0,115,234,0.35)',
          }}>
          <span aria-hidden="true" style={{ fontSize: 16 }}>🔍</span>
          {listing ? 'Listeleniyor…' : 'Listele'}
        </button>
      </div>

      {areaOpen && (
        <div style={s.overlay} onClick={() => setAreaOpen(false)}>
          <div style={{ ...s.modal, maxWidth: 920, width: '94%' }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <h3 style={s.modalTitle}>Haritadan Sevkiyat Alanı Seç</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#6b7280)' }}>
                  {baseLoading ? 'Bekleyen siparişli müşteriler yükleniyor…'
                    : `${baseCustomers.length} bekleyen siparişli müşteri. Fareyle basılı tutup “kalemle çizer gibi” bir veya birden çok alan çizin.`}
                </p>
              </div>
              <button style={s.closeBtn} onClick={() => setAreaOpen(false)}>×</button>
            </div>
            <div style={{ ...s.modalBody, padding: 16 }}>
              <ShipmentAreaMap
                customers={baseCustomers}
                polygons={polygons}
                onPolygonsChange={setPolygons}
                height={460}
              />
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setPolygons([])}>Alanları Temizle</button>
              <button style={s.btnPrimary} onClick={() => setAreaOpen(false)}>Tamam ({areaCount} alan)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function vehicleChip(active, disabled, isAll) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 999,
    border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-color,#cbd5e1)'}`,
    background: active ? 'rgba(0,115,234,0.10)' : 'var(--input-bg,#fff)',
    color: active ? 'var(--accent)' : 'var(--text-primary,#334155)',
    fontWeight: isAll ? 800 : 700, fontSize: 12.5, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
  };
}
